import Candidate from '@/orm/models/Candidate/Candidate';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import router from '@/router';

const NOTIFICATION_LINK_CONFIG = {
  'Статус кандидата обновлён': {
    routeToIdType: 'candidateId',
    candidatePattern: /^(.+?) завершил/,
  },
  'Новый отклик': {
    routeToIdType: 'candidateId',
    candidatePattern: /кандидата (.+)\./,
    vacancyPattern: /«(.+?)»/,
  },
  'Сообщение от кандидата': {
    routeToIdType: 'candidateId',
    contactTab: true,
    candidatePattern: /Кандидат (.+?) отправил/,
    vacancyPattern: /по вакансии «(.+?)»/,
  },
  'Вакансия создана': {
    routeToIdType: 'vacancyId',
    vacancyPattern: /«(.+?)»/,
  },
  'Вакансия восстановлена': {
    routeToIdType: 'vacancyId',
    vacancyPattern: /«(.+?)»/,
  },
  'Вакансия опубликована': {
    routeToIdType: 'vacancyId',
    vacancyPattern: /[«"](.+?)[»"]/,
  },
};

const TARIFF_PLAN_PATTERN = /тарифный план/i;

const STATIC_TEXT_LINK_CONFIG = {
  'Карта добавлена': {
    pattern: /успешно добавлена/i,
    linkType: 'paymentInfo',
  },
  'Подписка отменена': {
    pattern: /тарифа будет недоступен с .+\./i,
    linkType: 'tariff',
  },
  'Пробный период активирован': {
    pattern: /Пробный период успешно подключён/i,
    linkType: 'currentPlan',
  },
};

const VACANCY_QUOTE_VARIANTS = (title) => [
  `«${title}»`,
  `"${title}"`,
  `'${title}'`,
];

function findVacancyQuoteRange(text, title) {
  const variants = VACANCY_QUOTE_VARIANTS(title);

  for (let index = 0; index < variants.length; index += 1) {
    const quotedVacancy = variants[index];
    const quotedVacancyIndex = text.indexOf(quotedVacancy);

    if (quotedVacancyIndex !== -1) {
      return {
        start: quotedVacancyIndex,
        end: quotedVacancyIndex + quotedVacancy.length,
        value: quotedVacancy,
      };
    }
  }

  const titleIndex = text.indexOf(title);

  if (titleIndex !== -1) {
    return {
      start: titleIndex,
      end: titleIndex + title.length,
      value: title,
    };
  }

  return null;
}

function normalizeRouteId(id) {
  if (id === null || id === undefined) {
    return null;
  }

  return String(id);
}

function getCandidateVacancyId(candidate) {
  return candidate?.vacancyId ?? candidate?.vacancy?.id ?? null;
}

function findCachedCandidate(candidateId) {
  return Candidate.find(candidateId) || Candidate.find(Number(candidateId));
}

async function ensureCandidate(candidateId) {
  if (!candidateId) {
    return null;
  }

  let candidate = findCachedCandidate(candidateId);

  if (!getCandidateVacancyId(candidate)) {
    try {
      await Candidate.api().fetchById(candidateId);
    } catch (e) {
      return null;
    }

    candidate = findCachedCandidate(candidateId);
  }

  return candidate;
}

async function resolveVacancyId(candidateId) {
  const cachedVacancyId = normalizeRouteId(getCandidateVacancyId(findCachedCandidate(candidateId)));

  if (cachedVacancyId) {
    return cachedVacancyId;
  }

  const candidate = await ensureCandidate(candidateId);

  return normalizeRouteId(getCandidateVacancyId(candidate));
}

function getCandidateCardRoute(candidateId, vacancyId, { contactTab = false } = {}) {
  const normalizedVacancyId = normalizeRouteId(vacancyId);
  const normalizedCandidateId = normalizeRouteId(candidateId);

  if (!normalizedCandidateId || !normalizedVacancyId) {
    return null;
  }

  return {
    name: 'candidate',
    params: {
      id: normalizedVacancyId,
      candidateId: normalizedCandidateId,
    },
    ...(contactTab ? { query: { tab: 'contact' } } : {}),
  };
}

function getVacancyRoute(vacancyId) {
  const normalizedVacancyId = normalizeRouteId(vacancyId);

  if (!normalizedVacancyId) {
    return null;
  }

  return {
    name: 'candidates',
    params: {
      id: normalizedVacancyId,
    },
  };
}

function findVacancy(vacancyId) {
  const normalizedVacancyId = normalizeRouteId(vacancyId);

  if (!normalizedVacancyId) {
    return null;
  }

  return Vacancy.find(normalizedVacancyId)
    || Vacancy.find(Number(normalizedVacancyId));
}

function prefetchVacancyIfMissing(vacancyId) {
  if (!vacancyId || findVacancy(vacancyId)) {
    return;
  }

  Vacancy.api().fetchById(vacancyId).catch(() => {});
}

async function ensureVacancy(vacancyId) {
  const normalizedVacancyId = normalizeRouteId(vacancyId);

  if (!normalizedVacancyId) {
    return false;
  }

  if (findVacancy(normalizedVacancyId)) {
    return true;
  }

  try {
    await Vacancy.api().fetchById(normalizedVacancyId);
  } catch (e) {
    return false;
  }

  return !!findVacancy(normalizedVacancyId);
}

async function waitUntilVacancyInStore(vacancyId) {
  const normalizedVacancyId = normalizeRouteId(vacancyId);

  if (!normalizedVacancyId || findVacancy(normalizedVacancyId)) {
    return;
  }

  const timeoutAt = Date.now() + 10000;

  while (Date.now() < timeoutAt) {
    if (findVacancy(normalizedVacancyId)) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

async function navigateToCandidateCard(candidateId, vacancyId, options) {
  const route = getCandidateCardRoute(candidateId, vacancyId, options);

  if (!route) {
    return;
  }

  const candidateCardPath = `/vacancy/${route.params.id}/candidates/${route.params.candidateId}`;
  const isOnVacancyCandidates = ['candidates', 'candidate'].includes(router.currentRoute.name)
    && normalizeRouteId(router.currentRoute.params.id) === route.params.id;

  if (!isOnVacancyCandidates) {
    await router.push(getVacancyRoute(vacancyId));
  }

  if (router.currentRoute.path === candidateCardPath) {
    return;
  }

  await router.push({
    path: candidateCardPath,
    ...(route.query ? { query: route.query } : {}),
  });
}

export async function openCandidateCard(candidateId, options) {
  const cachedVacancyId = normalizeRouteId(getCandidateVacancyId(findCachedCandidate(candidateId)));
  const vacancyId = cachedVacancyId || await resolveVacancyId(candidateId);

  if (!vacancyId) {
    return;
  }

  await ensureVacancy(vacancyId);
  ensureCandidate(candidateId).catch(() => {});
  await navigateToCandidateCard(candidateId, vacancyId, options);
  await waitUntilVacancyInStore(vacancyId);
}

export async function openVacancyFromCandidate(candidateId) {
  const cachedVacancyId = normalizeRouteId(getCandidateVacancyId(findCachedCandidate(candidateId)));

  if (cachedVacancyId) {
    prefetchVacancyIfMissing(cachedVacancyId);
    ensureCandidate(candidateId).catch(() => {});
    await router.push(getVacancyRoute(cachedVacancyId));
    return;
  }

  const vacancyId = await resolveVacancyId(candidateId);
  const route = getVacancyRoute(vacancyId);

  if (!route) {
    return;
  }

  prefetchVacancyIfMissing(vacancyId);
  await router.push(route);
}

export async function openTariffPlans() {
  await router.push({ name: 'settings', params: { type: 'payment', addType: 'plans' } });
}

export async function openPaymentInfo() {
  await router.push({ name: 'settings', params: { type: 'payment', addType: 'info' } });
}

export async function openCurrentPlan() {
  await router.push({ name: 'settings', params: { type: 'payment', addType: 'plan' } });
}

export async function openVacancyKanban(vacancyId) {
  const route = getVacancyRoute(vacancyId);

  if (!route) {
    return;
  }

  prefetchVacancyIfMissing(vacancyId);
  await router.push(route);
}

function rangesOverlap(a, b) {
  return a.start < b.end && b.start < a.end;
}

function findTariffPlanLinkRange(text) {
  const match = text.match(TARIFF_PLAN_PATTERN);

  if (!match) {
    return null;
  }

  return {
    start: match.index,
    end: match.index + match[0].length,
    linkType: 'tariff',
    value: match[0],
  };
}

function findStaticTextLinkRange(text, notificationHeader) {
  const config = STATIC_TEXT_LINK_CONFIG[notificationHeader];

  if (!config) {
    return null;
  }

  const match = text.match(config.pattern);

  if (!match) {
    return null;
  }

  return {
    start: match.index,
    end: match.index + match[0].length,
    linkType: config.linkType,
    value: match[0],
  };
}

function findNotificationLinkRanges(text, notification) {
  if (!text) {
    return [];
  }

  const ranges = [];
  const config = notification.routeToId
    ? NOTIFICATION_LINK_CONFIG[notification.notificationHeader]
    : null;

  if (config) {
    const { routeToIdType, contactTab } = config;

    if (config.candidatePattern) {
      const match = text.match(config.candidatePattern);
      const name = match?.[1]?.trim();

      if (name) {
        const nameIndex = text.indexOf(name);

        if (nameIndex !== -1) {
          ranges.push({
            start: nameIndex,
            end: nameIndex + name.length,
            linkType: 'candidate',
            value: name,
            routeToId: notification.routeToId,
            routeToIdType,
            contactTab: !!contactTab,
          });
        }
      }
    }

    if (config.vacancyPattern) {
      const match = text.match(config.vacancyPattern);
      const title = match?.[1]?.trim();

      if (title) {
        const vacancyQuoteRange = findVacancyQuoteRange(text, title);

        if (vacancyQuoteRange) {
          ranges.push({
            start: vacancyQuoteRange.start,
            end: vacancyQuoteRange.end,
            linkType: 'vacancy',
            value: vacancyQuoteRange.value,
            routeToId: notification.routeToId,
            routeToIdType,
            contactTab: false,
          });
        }
      }
    }
  }

  const tariffRange = findTariffPlanLinkRange(text);

  if (tariffRange && !ranges.some((range) => rangesOverlap(range, tariffRange))) {
    ranges.push(tariffRange);
  }

  const staticRange = findStaticTextLinkRange(text, notification.notificationHeader);

  if (staticRange && !ranges.some((range) => rangesOverlap(range, staticRange))) {
    ranges.push(staticRange);
  }

  return ranges.sort((a, b) => a.start - b.start);
}

export function buildNotificationTextParts(notification) {
  const text = notification.notificationText;

  if (!text) {
    return [];
  }

  const linkRanges = findNotificationLinkRanges(text, notification);

  if (!linkRanges.length) {
    return [{ type: 'text', value: text }];
  }

  const parts = [];
  let cursor = 0;

  linkRanges.forEach((range) => {
    if (range.start > cursor) {
      parts.push({ type: 'text', value: text.slice(cursor, range.start) });
    }

    parts.push({
      type: 'link',
      linkType: range.linkType,
      value: range.value,
      routeToId: range.routeToId,
      routeToIdType: range.routeToIdType,
      contactTab: range.contactTab,
    });

    cursor = range.end;
  });

  if (cursor < text.length) {
    parts.push({ type: 'text', value: text.slice(cursor) });
  }

  return parts;
}
